import { useMutation } from "@apollo/client";
import { Formik, FormikActions } from "formik";
import React, { useContext } from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";
import * as Yup from "yup";

import { DispatchContext } from "App";
import { ReactComponent as Icon } from "assets/forgot-password.svg";
import { StyledGrid } from "components/simple";
import { ResetPassword } from "graphql/user/user.gql";
import { ResetPasswordFormValues } from "models/user";
import { color } from "style/constants";
import { ResetForm } from "./Forms";
import { Callout, Title } from "./styled";

const initialFormValues = {
  newPassword: "",
  confirmPassword: ""
};

const PasswordReset: React.FC<RouteComponentProps> = ({ location }: RouteComponentProps) => {
  const { dispatch } = useContext(DispatchContext);
  const [resetPassword] = useMutation(ResetPassword);

  const ResetPasswordSchema = Yup.object().shape({
    newPassword: Yup.string().matches(
      /^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z]).{12,}$/,
      "Password must contain at least 12 characters, including one uppercase letter, one lowercase letter, and one number."
    ),
    confirmPassword: Yup.string().oneOf([Yup.ref("newPassword")], "Passwords must match")
  });

  const sessionToken = location.search.split("=")[1];
  if (!sessionToken) {
    return <Redirect to="/login" />;
  }

  const submitForm = async (
    values: ResetPasswordFormValues,
    actions: FormikActions<ResetPasswordFormValues>
  ): Promise<null> => {
    const { newPassword } = values;
    try {
      const res = await resetPassword({
        variables: {
          token: sessionToken,
          newPassword: newPassword
        }
      });
      if (res?.data && res.data?.resetPassword && res.data.resetPassword?.duoUrl) {
        const duoUrl = res.data.resetPassword.duoUrl;
        // redirect to duoUrl
        window.location.href = duoUrl;
        return null;
      }

      if (res.data && res.data.resetPassword && res.data.resetPassword.token === null) {
        dispatch({
          type: "error",
          title: "Please verify your email address",
          description:
            'Check your email inbox and click the link that reads "BrainHealth® dashboard"'
        });
      }
    } catch (error) {
      if (error?.graphQLErrors[0]?.extensions?.code === "BAD_PASSWORD") {
        actions.setFieldError(
          "newPassword",
          "You have used this password before, please try another one"
        );
      } else {
        dispatch({ type: "error", description: "Magic link may have expired" });
      }
      return null;
    } finally {
      actions.setSubmitting(false);
    }
    return null;
  };

  return (
    <>
      <StyledGrid backgroundColor={color.WHITE} container direction="column" fillRemainingHeight>
        <StyledGrid
          container
          centerContent
          direction="row"
          maxWidth="1086px"
          spacing={10}
          margin="auto"
          fillRemainingHeight
        >
          <StyledGrid alignSelf="center" item maxWidth="470px">
            <Icon width="100%" height="100%" />
          </StyledGrid>
          <StyledGrid
            container
            direction="column"
            item
            lg={6}
            justifyContent="center"
            textAlign="left"
            spacing={3}
          >
            <StyledGrid item>
              <Title>Forgot Password</Title>
              <Callout>Enter and confirm your new password.</Callout>
            </StyledGrid>
            <StyledGrid item>
              <Formik
                component={ResetForm}
                initialValues={initialFormValues}
                onSubmit={submitForm}
                validationSchema={ResetPasswordSchema}
                validateOnBlur
              />
            </StyledGrid>
          </StyledGrid>
        </StyledGrid>
      </StyledGrid>
    </>
  );
};

export default PasswordReset;
